import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React, { FC } from 'react'

import ImageByWidthDevice from '@/components/ImageByWidthDevice'
import { Gallery } from '@/types/gallery'
import { formatEpoch } from '@/utils/formatter'

interface AlbumCardProps {
  locale?: string
  data: Gallery
  href: string
}

const AlbumCard: FC<AlbumCardProps> = ({ data, href }) => {
  const router = useRouter()
  const { t } = useTranslation()

  return (
    <article className='group relative h-auto w-full lg:h-[409px] lg:w-[349px]'>
      <span className='absolute left-4 top-4 z-10 rounded-[100px] bg-orange px-2.5 py-1.5 text-[11px] font-medium leading-4 text-gray-1'>
        {data.sezRegionProfileName}
      </span>

      <div className='relative h-[277px] w-full overflow-hidden rounded-[4px] lg:h-[370px]'>
        <div className='absolute inset-0 z-[5] opacity-0 transition-all duration-500 group-hover:bg-grad-albumCard group-hover:opacity-80' />
        <ImageByWidthDevice
          images={data.imageFiles}
          alt={data.imageFiles.desktop.fileName ?? ''}
          className='h-full w-full object-cover object-center transition-all duration-500 group-hover:scale-110 lg:h-[370px]'
        />
      </div>

      <div className='z-50 flex min-h-[178px] w-full gap-3 overflow-hidden rounded-b-[4px] rounded-tr-[4px] bg-white p-4 pl-[17px] shadow-album-card transition-all duration-500 lg:absolute lg:-left-[1px] lg:bottom-0 lg:h-20 lg:w-[337px] lg:group-hover:h-[201px]'>
        <div className='flex flex-col border-r-[1px] border-neutral-border pr-3'>
          <span className='text-2xl font-bold leading-8 text-gray-1'>
            {formatEpoch(data.createdAt, 'dd', router.locale)}
          </span>

          <span className='truncate text-xs leading-normal text-gray-2'>
            {formatEpoch(data.createdAt, 'LLL yy', router.locale)}
          </span>
        </div>

        <div className='flex flex-col gap-3 lg:gap-0 lg:group-hover:h-[201px] lg:group-hover:gap-3'>
          <h6 className='line-clamp-2 h-12 text-base font-bold text-gray-1'>{data.title}</h6>

          <p className='line-clamp-2 h-12 text-sm leading-normal tracking-[-0.084px] text-gray-2 transition-all duration-500 lg:line-clamp-3 lg:h-0 lg:group-hover:h-[65px]'>
            {data.shortDescription}
          </p>

          <Link href={`${href}/${data.slug}`}>
            <button className='flex w-fit items-center gap-2 rounded-[100px] bg-red px-4 py-2 text-white transition-all duration-500 lg:hidden lg:group-hover:flex'>
              <span className='text-[13px] font-bold'>{t('album:card.button')}</span>

              <span className='kek-arrow-right text-base' />
            </button>
          </Link>
        </div>
      </div>
    </article>
  )
}

export default AlbumCard
